/**
 * Authentication utility functions
 */

import type { User } from "../model/authTypes";

/**
 * User may receive a JWT before all onboarding checks complete.
 * Only persist `auth_token` when the user is allowed to enter fully authenticated routes
 * (login + Google). Apple uses {@link isUserEligibleForApplePersistentAuthToken} (same rules + non-empty phone).
 */
export const isUserEligibleForPersistentAuthToken = (user: User): boolean =>
  !user.force_mobile_update &&
  !user.force_password_update &&
  // user.is_mail_verified &&
  user.is_phone_verified;

/** Apple login API persists `auth_token` only when base eligibility holds and `phone` is present (non-empty). */
export const isUserEligibleForApplePersistentAuthToken = (user: User): boolean =>
  isUserEligibleForPersistentAuthToken(user) && Boolean(user.phone?.trim());

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates email and returns validation result with error message
 */
export const validateEmailWithMessage = (email: string): ValidationResult => {
  if (!email || email.trim() === "") {
    return { isValid: false, error: "Email is required" };
  }
  if (!validateEmail(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }
  return { isValid: true };
};

/**
 * Validates password strength
 */
export const validatePassword = (password: string): boolean => {
  return password.length >= 8;
};

/**
 * Validates password and returns validation result with error message
 */
export const validatePasswordWithMessage = (password: string): ValidationResult => {
  if (!password || password.trim() === "") {
    return { isValid: false, error: "Password is required" };
  }
  if (password.length < 8) {
    return { isValid: false, error: "Password must be at least 8 characters long" };
  }
  return { isValid: true };
};

/**
 * Validates name (first name or last name)
 */
export const validateName = (name: string): boolean => {
  return name.trim().length >= 1;
};

/**
 * Validates name and returns validation result with error message
 */
export const validateNameWithMessage = (
  name: string,
  fieldName: string = "Name"
): ValidationResult => {
  if (!name || name.trim() === "") {
    return { isValid: false, error: `${fieldName} is required` };
  }
  if (name.trim().length < 1) {
    return { isValid: false, error: `${fieldName} cannot be empty` };
  }
  return { isValid: true };
};

/**
 * Validates that password and confirm password match
 */
export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword || confirmPassword.trim() === "") {
    return { isValid: false, error: "Please confirm your password" };
  }
  if (password !== confirmPassword) {
    return { isValid: false, error: "Passwords do not match" };
  }
  return { isValid: true };
};

/**
 * Validates login credentials
 */
export const validateLoginCredentials = (
  email: string,
  password: string
): {
  emailError: string | null;
  passwordError: string | null;
  isValid: boolean;
} => {
  const emailValidation = validateEmailWithMessage(email);
  const passwordValidation = validatePasswordWithMessage(password);

  return {
    emailError: emailValidation.error || null,
    passwordError: passwordValidation.error || null,
    isValid: emailValidation.isValid && passwordValidation.isValid,
  };
};

/**
 * Validates signup credentials
 */
export const validateSignupCredentials = (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  confirmPassword: string
): {
  firstNameError: string | null;
  lastNameError: string | null;
  emailError: string | null;
  passwordError: string | null;
  confirmPasswordError: string | null;
  isValid: boolean;
} => {
  const firstNameValidation = validateNameWithMessage(firstName, "First name");
  const lastNameValidation = validateNameWithMessage(lastName, "Last name");
  const emailValidation = validateEmailWithMessage(email);
  const passwordValidation = validatePasswordWithMessage(password);
  const confirmPasswordValidation = validatePasswordMatch(password, confirmPassword);

  return {
    firstNameError: firstNameValidation.error || null,
    lastNameError: lastNameValidation.error || null,
    emailError: emailValidation.error || null,
    passwordError: passwordValidation.error || null,
    confirmPasswordError: confirmPasswordValidation.error || null,
    isValid:
      firstNameValidation.isValid &&
      lastNameValidation.isValid &&
      emailValidation.isValid &&
      passwordValidation.isValid &&
      confirmPasswordValidation.isValid,
  };
};
